import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Loader from "../common/Loader";
import {
  deviceLogFileService,
  DevicePingResult,
  HeartbeatDiagnostics,
  OutageReport,
} from "../../api/services/deviceLogFileService";
import { formatDisplayDateTime } from "../../utils/dateUtils";

// Verdicts the TV's outage probe attributes to the venue's network.
const VENUE_VERDICTS = new Set([
  "NO_NETWORK",
  "ROUTER_UNREACHABLE",
  "DNS_FAILED",
  "NO_INTERNET",
]);
const FODXPERT_VERDICTS = new Set([
  "SERVER_UNREACHABLE",
  "AUTH_FAILED",
  "SERVER_ERROR",
]);

const signalLabel = (rssi: number): string => {
  if (rssi >= -60) return "good";
  if (rssi >= -70) return "fair";
  if (rssi >= -80) return "weak";
  return "very weak";
};

const formatDuration = (from?: string, to?: string | null): string => {
  if (!from || !to) return "-";
  const minutes = Math.round(
    (new Date(to).getTime() - new Date(from).getTime()) / 60000,
  );
  if (minutes < 1) return "<1m";
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

const causeBadge = (outage: OutageReport) => {
  const meta = outage.metadata;
  if (meta?.kind === "heartbeat_gap") {
    return {
      label:
        meta.gapCause === "device_rebooted" ? "Power / restart" : "App stopped",
      className: "bg-gray-100 text-gray-700",
    };
  }
  if (meta?.verdict && VENUE_VERDICTS.has(meta.verdict)) {
    return { label: "Venue network", className: "bg-amber-100 text-amber-800" };
  }
  if (meta?.verdict && FODXPERT_VERDICTS.has(meta.verdict)) {
    return { label: "FodXpert side", className: "bg-red-100 text-red-700" };
  }
  return { label: "Unknown", className: "bg-gray-100 text-gray-600" };
};

const Fact = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="flex flex-col">
    <span className="text-xs uppercase text-gray-500">{label}</span>
    <span className="text-sm text-gray-800">{value}</span>
  </div>
);

const LatestSnapshot = ({ d }: { d: HeartbeatDiagnostics }) => {
  const network =
    d.transport === "wifi"
      ? `WiFi${d.ssid ? ` "${d.ssid}"` : ""}`
      : d.transport === "ethernet"
        ? "Ethernet"
        : (d.transport ?? "-");
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      <Fact label="Network" value={network} />
      <Fact
        label="WiFi signal"
        value={d.rssi != null ? `${d.rssi} dBm (${signalLabel(d.rssi)})` : "-"}
      />
      <Fact
        label="Internet (Android check)"
        value={
          d.internetValidated == null
            ? "-"
            : d.internetValidated
              ? "OK"
              : "No access"
        }
      />
      <Fact
        label="MQTT"
        value={
          d.mqttConnected == null
            ? "-"
            : `${d.mqttConnected ? "Connected" : "Disconnected"}${
                d.mqttReconnects ? ` · ${d.mqttReconnects} reconnects` : ""
              }`
        }
      />
      <Fact label="App version" value={d.appVersion ?? "-"} />
      <Fact
        label="Box uptime"
        value={
          d.deviceUptimeSeconds != null
            ? formatDuration(
                new Date(0).toISOString(),
                new Date(d.deviceUptimeSeconds * 1000).toISOString(),
              )
            : "-"
        }
      />
      <Fact
        label="Logs waiting on box"
        value={
          d.queuedLogChunks == null ? (
            "-"
          ) : d.queuedLogChunks > 3 ? (
            <span className="text-amber-700">
              {d.queuedLogChunks} files (upload is behind)
            </span>
          ) : (
            `${d.queuedLogChunks} file${d.queuedLogChunks === 1 ? "" : "s"}`
          )
        }
      />
      <Fact
        label="Snapshot received"
        value={d.receivedAt ? formatDisplayDateTime(d.receivedAt) : "-"}
      />
    </div>
  );
};

const PING_ERROR_TEXT: Record<string, string> = {
  NO_REPLY:
    "No reply in 10s. The box is off or offline, its MQTT connection is down, or the app is not on the player screen.",
  BROKER_UNAVAILABLE:
    "Our server is not connected to the MQTT broker right now (FodXpert side).",
  NOT_SUPPORTED: "Raspberry Pi players do not answer pings yet.",
  NOT_ASSIGNED: "This device is not linked to a screen.",
};

const PingOutcome = ({ result }: { result: DevicePingResult }) =>
  result.ok ? (
    <p className="text-sm text-green-700">
      Replied in {result.roundTripMs} ms over MQTT. The box is online and
      reachable right now. It is uploading its latest logs; they appear below
      within a minute.
    </p>
  ) : (
    <p className="text-sm text-red-600">
      {PING_ERROR_TEXT[result.error ?? ""] ?? `Ping failed (${result.error}).`}
    </p>
  );

/**
 * Why a screen showed OFF: the latest heartbeat snapshot plus the outages the
 * TV diagnosed on its own and reported once it was back online.
 */
export default function DeviceConnectivityPanel({
  deviceId,
  enabled,
}: {
  deviceId: string;
  enabled: boolean;
}) {
  const query = useQuery({
    queryKey: ["deviceConnectivity", deviceId],
    queryFn: () => deviceLogFileService.getConnectivity(deviceId),
    enabled: enabled && !!deviceId,
    refetchInterval: 60_000,
  });

  const data = query.data;

  const queryClient = useQueryClient();
  const ping = useMutation({
    mutationFn: () => deviceLogFileService.ping(deviceId),
    // A reply refreshes lastDiagnostics on the server.
    onSuccess: (result) => {
      if (result.ok) {
        queryClient.invalidateQueries({
          queryKey: ["deviceConnectivity", deviceId],
        });
        // The box flushes its logs on ping; pick them up once they land.
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ["deviceLogSummary", deviceId] });
          queryClient.invalidateQueries({ queryKey: ["deviceLogFiles", deviceId] });
        }, 15_000);
      }
    },
  });

  return (
    <div className="mb-4 flex flex-col gap-3 rounded-lg border border-gray-200 p-3">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-gray-800">Connectivity</h4>
        <button
          type="button"
          className="rounded bg-primary-default px-3 py-1.5 text-sm text-white hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
          disabled={ping.isPending}
          onClick={() => ping.mutate()}
        >
          {ping.isPending ? "Pinging…" : "Ping device"}
        </button>
      </div>
      {ping.data && <PingOutcome result={ping.data} />}
      {ping.isError && (
        <p className="text-sm text-red-600">
          Ping request failed: {(ping.error as Error).message}
        </p>
      )}
      {query.isLoading ? (
        <div className="flex justify-center py-4">
          <Loader size="small" />
        </div>
      ) : query.isError ? (
        <p className="text-sm text-red-500">Failed to load connectivity.</p>
      ) : !data?.lastDiagnostics && !data?.outages.length ? (
        <p className="text-sm text-gray-500">
          No diagnostics from this device yet. Only Android TV boxes on the
          latest app version report them.
        </p>
      ) : (
        <>
          {data.lastDiagnostics && <LatestSnapshot d={data.lastDiagnostics} />}

          <div>
            <h5 className="mb-1 text-xs font-semibold uppercase text-gray-500">
              Outages reported by the device
            </h5>
            {data.outages.length === 0 ? (
              <p className="text-sm text-gray-500">None reported.</p>
            ) : (
              <div className="max-h-64 overflow-auto rounded border border-gray-200">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50 text-left text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2">Started (IST)</th>
                      <th className="px-3 py-2">Duration</th>
                      <th className="px-3 py-2">Cause</th>
                      <th className="px-3 py-2">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {data.outages.map((outage) => {
                      const badge = causeBadge(outage);
                      return (
                        <tr key={outage.id} className="text-gray-700">
                          <td className="whitespace-nowrap px-3 py-2">
                            {formatDisplayDateTime(
                              outage.metadata?.startedAt ?? outage.createdAt,
                            )}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2">
                            {formatDuration(
                              outage.metadata?.startedAt,
                              outage.metadata?.endedAt,
                            )}
                          </td>
                          <td className="whitespace-nowrap px-3 py-2">
                            <span
                              className={`rounded px-2 py-0.5 text-xs font-medium ${badge.className}`}
                            >
                              {badge.label}
                            </span>
                          </td>
                          <td className="px-3 py-2">{outage.message}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <p className="mt-1 text-xs text-gray-400">
              The box tests its WiFi, router, DNS and our server while it is
              offline and uploads the result when it reconnects. Times come from
              the device clock.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
